import { memo, useEffect, useState, useRef, useCallback } from 'react';
import { Activity, Clock, CheckCircle, XCircle, Loader2, RefreshCw, ChevronDown, ChevronUp, OctagonPause, Ban } from 'lucide-react';
import { useExecutions } from '../../hooks/useExecutions';
import { stopExecution } from '../../services/stopExecution';

const STATUS_CONFIG = {
  queued: { label: 'En cola', icon: Clock, color: '#f59e0b' },
  processing: { label: 'Procesando', icon: Loader2, color: '#3b82f6' },
  completed: { label: 'Completado', icon: CheckCircle, color: '#10b981' },
  failed: { label: 'Error', icon: XCircle, color: '#ef4444' },
  cancelled: { label: 'Detenido', icon: Ban, color: '#6b7280' },
};

const RESULT_TYPE_LABELS = {
  enhance_photo: 'Mejorar Imagen',
  create_video: 'Crear Video',
  nano_banana: 'Nano Banana',
  background_removal: 'Remover Fondo',
  video_with_sound: 'Video + Audio',
  fabric_avatar: 'Avatar Parlante',
  dream_actor: 'DreamActor',
  lyria2: 'Lyria 2',
  qwen3_tts: 'Qwen3 TTS',
  style_transfer: 'Transferir Estilo',
  upscale: 'Upscale',
  frame_extractor: 'Frame Extractor',
  lyria3_pro: 'Lyria 3 Full Song',
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const formatDuration = (seconds) => {
  if (!seconds) return '-';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
};

const ExecutionsPanel = memo(() => {
  const { listExecutions, getStats } = useExecutions();
  const [executions, setExecutions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [stopping, setStopping] = useState({});
  const refreshTimer = useRef(null);
  const mountedRef = useRef(true);

  const fetchData = useCallback(async () => {
    // Retry for auth timing (axios interceptor may not be ready on first mount)
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        setLoading(true);
        const [execList, statsData] = await Promise.all([
          listExecutions(20),
          getStats().catch(() => null),
        ]);
        if (mountedRef.current) {
          setExecutions(execList || []);
          setStats(statsData);
        }
        if (mountedRef.current) setLoading(false);
        return; // success
      } catch (err) {
        if (err.response?.status === 401 && attempt < 2) {
          await new Promise(r => setTimeout(r, 800));
          continue;
        }
        console.error('[ExecutionsPanel] Fetch error:', err.message);
      }
    }
    if (mountedRef.current) setLoading(false);
  }, [listExecutions, getStats]);

  // Auto-refresh every 5s if there are active executions
  useEffect(() => {
    mountedRef.current = true;
    fetchData();

    const scheduleRefresh = () => {
      refreshTimer.current = setTimeout(async () => {
        if (!mountedRef.current) return;
        await fetchData();
        scheduleRefresh();
      }, 5000);
    };
    scheduleRefresh();

    return () => {
      mountedRef.current = false;
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [fetchData]);

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Detener: optimista (la fila pasa a "Detenido" al instante) + refresco real.
  const handleStop = useCallback(async (event, execId) => {
    event.stopPropagation();
    setStopping(prev => ({ ...prev, [execId]: true }));
    const ok = await stopExecution(execId);
    if (ok && mountedRef.current) {
      setExecutions(prev => prev.map(e => (
        e.id === execId
          ? { ...e, status: 'cancelled', error_message: 'Detenida por el usuario' }
          : e)));
      window.dispatchEvent(new Event('executions:changed'));
    }
    if (mountedRef.current) {
      setStopping(prev => ({ ...prev, [execId]: false }));
      fetchData();
    }
  }, [fetchData]);

  const activeCount = executions.filter(e => e.status === 'queued' || e.status === 'processing').length;

  return (
    <div className="executions-panel" data-testid="executions-panel">
      {/* Header */}
      <div className="executions-panel-header">
        <h3>
          <Activity size={16} />
          Ejecuciones
          {activeCount > 0 && (
            <span className="exec-active-badge" data-testid="exec-active-count">{activeCount}</span>
          )}
        </h3>
        <button
          className="refresh-btn"
          onClick={fetchData}
          disabled={loading}
          data-testid="exec-refresh-btn"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Stats Row */}
      {stats && (
        <div className="exec-stats-row" data-testid="exec-stats">
          {Object.entries(STATUS_CONFIG).filter(([k]) => k !== 'cancelled').map(([key, cfg]) => {
            const count = stats[key] || 0;
            return (
              <div key={key} className="exec-stat-item" style={{ '--stat-color': cfg.color }}>
                <cfg.icon size={12} className={key === 'processing' ? 'animate-spin' : ''} />
                <span className="exec-stat-count">{count}</span>
                <span className="exec-stat-label">{cfg.label}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Execution List */}
      <div className="exec-list" data-testid="exec-list">
        {executions.length === 0 ? (
          <div className="exec-empty">
            <Activity size={24} />
            <span>Sin ejecuciones</span>
          </div>
        ) : (
          executions.map((exec) => {
            const cfg = STATUS_CONFIG[exec.status] || STATUS_CONFIG.queued;
            const StatusIcon = cfg.icon;
            const isExpanded = expanded[exec.id];

            return (
              <div
                key={exec.id}
                className={`exec-item exec-status-${exec.status}`}
                data-testid={`exec-item-${exec.id}`}
              >
                <div className="exec-item-main" onClick={() => toggleExpand(exec.id)}>
                  <StatusIcon
                    size={14}
                    style={{ color: cfg.color }}
                    className={exec.status === 'processing' ? 'animate-spin' : ''}
                  />
                  <div className="exec-item-info">
                    <span className="exec-item-type">
                      {RESULT_TYPE_LABELS[exec.result_type] || exec.result_type}
                    </span>
                    <span className="exec-item-time">{formatTime(exec.created_at)}</span>
                  </div>
                  <div className="exec-item-meta">
                    {(exec.status === 'queued' || exec.status === 'processing') && (
                      <button
                        type="button"
                        className="exec-stop-btn"
                        onClick={(event) => handleStop(event, exec.id)}
                        disabled={!!stopping[exec.id]}
                        title="Detener esta generación (no se te cobra)"
                        data-testid={`exec-stop-${exec.id}`}
                      >
                        {stopping[exec.id]
                          ? <Loader2 size={11} className="animate-spin" />
                          : <OctagonPause size={11} />}
                        Detener
                      </button>
                    )}
                    <span className="exec-item-status" style={{ color: cfg.color }}>
                      {cfg.label}
                    </span>
                    {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </div>
                </div>

                {isExpanded && (
                  <div className="exec-item-details" data-testid={`exec-details-${exec.id}`}>
                    <div className="exec-detail-row">
                      <span>ID:</span>
                      <code>{exec.id.slice(0, 12)}...</code>
                    </div>
                    {exec.duration_seconds && (
                      <div className="exec-detail-row">
                        <span>Duración:</span>
                        <span>{formatDuration(exec.duration_seconds)}</span>
                      </div>
                    )}
                    {exec.credits_used > 0 && (
                      <div className="exec-detail-row">
                        <span>Créditos:</span>
                        <span>{exec.credits_used}</span>
                      </div>
                    )}
                    {exec.error_message && (
                      <div className="exec-detail-row exec-error-msg">
                        <span>Error:</span>
                        <span>{exec.error_message}</span>
                      </div>
                    )}
                    {exec.results && exec.results.length > 0 && exec.results[0].output_url && (
                      <div className="exec-detail-row">
                        <span>Output:</span>
                        <a href={exec.results[0].output_url} target="_blank" rel="noopener noreferrer" className="exec-output-link">
                          Ver resultado
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
});

ExecutionsPanel.displayName = 'ExecutionsPanel';

export default ExecutionsPanel;
